/**
 * src/lib/hooks/useTaskData.js — Task domain hook.
 *
 * Owns: tasks state, tasksRef mirror, pending status queue,
 *       all task mutation callbacks (update, create, delete, archive).
 *
 * Extracted from useAppData.js (Phase 81-01) for domain isolation,
 * independent testability, and observability.
 */

import { useState, useCallback, useRef } from 'react';
import { fetchTasks, updateTask, updateTaskStatus, updateTaskAssignee, createTask, deleteTask, archiveTask } from '../api/tasks';
import { fetchTimeLog, createTimeEntry } from '../api/timelog';
import { normalizeTaskStatus } from '../constants/taskStatus';
import { logAction, logResult } from '../observability';
import { logAppError } from '../errorReporter';
import { mergeWithOptimistic } from '../mergeWithOptimistic';
import { logMergeDecision } from '../observability';
import { reconcileFetch } from '../offline/taskCache';
import { getOnline } from '../offline/networkStatus';
import { enqueue } from '../offline/outbox';

// Stable client id for offline-created tasks so create+edit chains share one id
// and replay is PK-idempotent. crypto.randomUUID in browsers; fallback otherwise.
function genOfflineId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'off_' + Date.now() + '_' + Math.random().toString(36).slice(2);
}

export function useTaskData({ scheduleReload, showError, offlineEnabled = false }) {
  const [tasks, setTasks] = useState([]);
  const [timeLogs, setTimeLogs] = useState([]);
  const tasksRef = useRef([]);
  const pendingStatusUpdates = useRef(new Map());

  // Keep tasksRef in sync for synchronous reads in mutation handlers.
  tasksRef.current = tasks;

  const handleUpdateTask = useCallback(async (taskId, updates) => {
    const cid = logAction('tasks:updateTask', { taskId, fields: Object.keys(updates) });
    let previousTask = null;

    if (updates.start !== undefined || updates.due !== undefined || updates.start_date !== undefined || updates.due_date !== undefined) {
      // Caller must pass optimisticTimestamps externally if needed
    }

    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      previousTask = t;
      const next = { ...t };
      if (updates.status !== undefined) next.status = normalizeTaskStatus(updates.status);
      if (updates.assignee_id !== undefined) next.assignee_id = updates.assignee_id || null;
      if (updates.due !== undefined) next.due_date = updates.due;
      if (updates.due_date !== undefined) next.due_date = updates.due_date;
      if (updates.start !== undefined) next.start_date = updates.start;
      if (updates.start_date !== undefined) next.start_date = updates.start_date;
      if (updates.notes !== undefined) next.notes = updates.notes;
      if (updates.estimated_hours !== undefined) next.estimated_hours = updates.estimated_hours;
      if (updates.task_name !== undefined) next.task_name = updates.task_name;
      if (updates.department !== undefined) next.department = updates.department;
      if (updates.project_id !== undefined) next.project_id = updates.project_id || null;
      if (updates.bundle_id !== undefined) next.bundle_id = updates.bundle_id || null;
      if (updates.priority !== undefined) next.priority = updates.priority || null;
      if (updates.task_order !== undefined) next.task_order = updates.task_order;
      if (updates.stage_id !== undefined) next.stage_id = updates.stage_id || null;
      return next;
    }));

    if (offlineEnabled && !getOnline()) {
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, _pendingSync: true } : t));
      await enqueue({ domain: 'tasks', type: 'update', entity_id: taskId, payload: updates });
      logResult('tasks:updateTask', 'queued', { correlationId: cid });
      return { success: true, queued: true };
    }

    try {
      const result = await updateTask({ record_id: taskId, ...updates });
      if (result?.success) {
        logResult('tasks:updateTask', 'success', { correlationId: cid });
        scheduleReload(2000, 'updateTask');
      } else if (previousTask) {
        setTasks(prev => prev.map(t => t.id === taskId ? previousTask : t));
        logResult('tasks:updateTask', 'rollback', { correlationId: cid, error: result?.error });
      }
      return result;
    } catch (e) {
      logAppError(e, 'useTaskData:updateTask');
      logResult('tasks:updateTask', 'error', { correlationId: cid, error: e.message });
      if (previousTask) {
        setTasks(prev => prev.map(t => t.id === taskId ? previousTask : t));
      }
      return { success: false, error: e.message };
    }
  }, [scheduleReload, offlineEnabled]);

  const handleUpdateStatus = useCallback(async (taskId, newStatus) => {
    const slug = normalizeTaskStatus(newStatus);

    if (typeof taskId === 'string' && taskId.startsWith('__pending_')) {
      const queue = pendingStatusUpdates.current.get(taskId) || [];
      pendingStatusUpdates.current.set(taskId, [...queue, slug]);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: slug } : t));
      return;
    }

    const cid = logAction('tasks:updateStatus', { taskId, newStatus: slug });
    const previousStatus = (tasksRef.current.find(t => t.id === taskId) || {}).status ?? null;
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: slug } : t));

    if (offlineEnabled && !getOnline()) {
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, _pendingSync: true } : t));
      await enqueue({ domain: 'tasks', type: 'status', entity_id: taskId, payload: { status: slug } });
      logResult('tasks:updateStatus', 'queued', { correlationId: cid });
      return;
    }

    try {
      const result = await updateTaskStatus(taskId, slug);
      if (result?.success) {
        logResult('tasks:updateStatus', 'success', { correlationId: cid });
        scheduleReload(2000, 'updateStatus');
      } else {
        if (previousStatus !== null) {
          setTasks(ts => ts.map(t => t.id === taskId ? { ...t, status: previousStatus } : t));
        }
        logResult('tasks:updateStatus', 'rollback', { correlationId: cid, error: result?.error });
        showError(result?.error || 'Failed to update task status');
      }
    } catch (e) {
      if (previousStatus !== null) {
        setTasks(ts => ts.map(t => t.id === taskId ? { ...t, status: previousStatus } : t));
      }
      logAppError(e, 'useTaskData:updateStatus');
      logResult('tasks:updateStatus', 'error', { correlationId: cid, error: e.message });
      showError(e.message || 'Failed to update task status');
    }
  }, [scheduleReload, showError, offlineEnabled]);

  // updateTaskAssignee RETURNS { success:false } on a refused RPC instead of throwing,
  // so the outcome is read from the result (same shape as handleUpdateStatus): roll
  // back, toast, and hand the result to the caller. The previous assignee is read from
  // tasksRef — a value captured inside a setState updater is not reliable.
  // opts.notify=false: the caller shows its own (localised) failure message — no generic toast.
  const handleUpdateAssignee = useCallback(async (taskId, assigneeId, { notify = true } = {}) => {
    const cid = logAction('tasks:updateAssignee', { taskId, assigneeId });
    const row = tasksRef.current.find(t => t.id === taskId);
    const previous = row ? (row.assignee_id ?? null) : undefined;
    const rollback = () => {
      if (previous !== undefined) setTasks(ts => ts.map(t => t.id === taskId ? { ...t, assignee_id: previous } : t));
    };

    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, assignee_id: assigneeId || null } : t));

    if (offlineEnabled && !getOnline()) {
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, _pendingSync: true } : t));
      await enqueue({ domain: 'tasks', type: 'assignee', entity_id: taskId, payload: { assignee_id: assigneeId } });
      logResult('tasks:updateAssignee', 'queued', { correlationId: cid });
      return { success: true, queued: true };
    }

    try {
      const result = await updateTaskAssignee(taskId, assigneeId);
      if (result?.success) {
        logResult('tasks:updateAssignee', 'success', { correlationId: cid });
        scheduleReload(2000, 'updateAssignee');
      } else {
        rollback();
        logResult('tasks:updateAssignee', 'rollback', { correlationId: cid, error: result?.error });
        if (notify) showError(result?.error || 'Failed to reassign task');
      }
      return result || { success: false, error: 'No response' };
    } catch (e) {
      rollback();
      logAppError(e, 'useTaskData:updateAssignee');
      logResult('tasks:updateAssignee', 'error', { correlationId: cid, error: e.message });
      if (notify) showError(e.message || 'Failed to reassign task');
      return { success: false, error: e.message };
    }
  }, [scheduleReload, showError, offlineEnabled]);

  const handleCreateTask = useCallback(async (payload) => {
    const cid = logAction('tasks:createTask', { task_name: payload.task_name });
    const tempId = '__pending_' + Date.now();
    const optimistic = {
      id: tempId,
      task_name: payload.task_name || '',
      status: normalizeTaskStatus(payload.status || 'todo'),
      department: payload.department || null,
      assignee_id: payload.assignee_id || null,
      due_date: payload.due_date || payload.due || null,
      start_date: payload.start_date || payload.start || null,
      project_id: payload.project_id || null,
      bundle_id: payload.bundle_id || null,
      deal_id: payload.deal_id || null, // lifecycle: show under the deal optimistically
      stage_id: payload.stage_id || null,
      estimated_hours: payload.estimated_hours || null,
      task_order: 0,
      archived_at: null,
      user_profiles: null,
      projects: null,
      stages: null,
    };
    setTasks(prev => [optimistic, ...prev]);

    if (offlineEnabled && !getOnline()) {
      const offlineId = genOfflineId();
      const realRow = { ...optimistic, id: offlineId, _pendingSync: true };
      setTasks(prev => prev.map(t => t.id === tempId ? realRow : t));
      const queued = pendingStatusUpdates.current.get(tempId);
      pendingStatusUpdates.current.delete(tempId);
      await enqueue({ domain: 'tasks', type: 'create', entity_id: offlineId, payload: { ...payload, id: offlineId } });
      for (const s of (queued || [])) {
        await enqueue({ domain: 'tasks', type: 'status', entity_id: offlineId, payload: { status: s } });
      }
      logResult('tasks:createTask', 'queued', { correlationId: cid });
      return { success: true, queued: true, record: realRow };
    }

    try {
      const result = await createTask(payload);
      if (result?.success) {
        if (result.record) {
          const realId = result.record.id;
          setTasks(prev => prev.map(t => t.id === tempId ? result.record : t));
          const queued = pendingStatusUpdates.current.get(tempId);
          if (queued?.length) {
            pendingStatusUpdates.current.delete(tempId);
            for (const queuedSlug of queued) {
              updateTaskStatus(realId, queuedSlug).catch(e => {
                logAppError(e, 'useTaskData:queuedStatusReplay');
              });
            }
          }
        } else {
          pendingStatusUpdates.current.delete(tempId);
          setTasks(prev => prev.filter(t => t.id !== tempId));
        }
        logResult('tasks:createTask', 'success', { correlationId: cid });
        scheduleReload(500, 'createTask');
      } else {
        pendingStatusUpdates.current.delete(tempId);
        setTasks(prev => prev.filter(t => t.id !== tempId));
        logResult('tasks:createTask', 'rollback', { correlationId: cid, error: result?.error });
        showError(result?.error || 'Failed to create task');
      }
      return result;
    } catch (e) {
      pendingStatusUpdates.current.delete(tempId);
      setTasks(prev => prev.filter(t => t.id !== tempId));
      logAppError(e, 'useTaskData:createTask');
      logResult('tasks:createTask', 'error', { correlationId: cid, error: e.message });
      showError(e.message || 'Failed to create task');
      return { success: false, error: e.message };
    }
  }, [scheduleReload, showError, offlineEnabled]);

  const handleDeleteTask = useCallback(async (taskId) => {
    const cid = logAction('tasks:deleteTask', { taskId });
    let removed = null;
    setTasks(prev => {
      removed = prev.find(t => t.id === taskId);
      return prev.filter(t => t.id !== taskId);
    });

    if (offlineEnabled && !getOnline()) {
      await enqueue({ domain: 'tasks', type: 'archive', entity_id: taskId, payload: {} });
      logResult('tasks:deleteTask', 'queued', { correlationId: cid });
      return;
    }

    try {
      await deleteTask(taskId);
      logResult('tasks:deleteTask', 'success', { correlationId: cid });
      scheduleReload(2000, 'deleteTask');
    } catch (e) {
      if (removed) setTasks(prev => [...prev, removed]);
      logAppError(e, 'useTaskData:deleteTask');
      logResult('tasks:deleteTask', 'error', { correlationId: cid, error: e.message });
    }
  }, [scheduleReload, offlineEnabled]);

  const handleArchiveTask = useCallback(async (taskId) => {
    const cid = logAction('tasks:archiveTask', { taskId });
    let removed = null;
    setTasks(prev => {
      removed = prev.find(t => t.id === taskId);
      return prev.filter(t => t.id !== taskId);
    });

    if (offlineEnabled && !getOnline()) {
      await enqueue({ domain: 'tasks', type: 'archive', entity_id: taskId, payload: {} });
      logResult('tasks:archiveTask', 'queued', { correlationId: cid });
      return { success: true, queued: true };
    }

    try {
      const result = await archiveTask(taskId);
      if (result?.success) {
        logResult('tasks:archiveTask', 'success', { correlationId: cid });
        scheduleReload(2000, 'archiveTask');
      } else if (removed) {
        setTasks(prev => [...prev, removed]);
        logResult('tasks:archiveTask', 'rollback', { correlationId: cid });
      }
      return result;
    } catch (e) {
      if (removed) setTasks(prev => [...prev, removed]);
      logAppError(e, 'useTaskData:archiveTask');
      logResult('tasks:archiveTask', 'error', { correlationId: cid, error: e.message });
    }
  }, [scheduleReload, offlineEnabled]);

  const handleCreateTimeEntry = useCallback(async (payload, options) => {
    const cid = logAction('tasks:createTimeEntry', { task_id: payload.task_id });
    try {
      const result = await createTimeEntry(payload, options);
      if (result?.success) {
        logResult('tasks:createTimeEntry', 'success', { correlationId: cid });
        scheduleReload(500, 'createTimeEntry');
      }
      return result;
    } catch (e) {
      logAppError(e, 'useTaskData:createTimeEntry');
      logResult('tasks:createTimeEntry', 'error', { correlationId: cid, error: e.message });
      return { success: false, error: e.message };
    }
  }, [scheduleReload, offlineEnabled]);

  // ─── Fetch / Apply / Merge (Phase 81-02) ───────────────────
  const fetchAll = useCallback(async () => {
    const [fetchedTasks, timeLogs] = await Promise.all([
      fetchTasks().catch(e => { console.warn('[TaskData] Tasks failed:', e.message); return []; }),
      fetchTimeLog().catch(() => []),
    ]);

    // Offline-tasks (flag-gated): write-through to the local cache when online,
    // hydrate from it when offline. Best-effort — any failure falls back to the
    // live result so the online path is never degraded. See taskCache.reconcileFetch.
    if (offlineEnabled) {
      try {
        const { tasks } = await reconcileFetch({ fetched: fetchedTasks, online: getOnline() });
        return { tasks, timeLogs };
      } catch {
        // spine-emit-ok: offline cache is best-effort; live result is authoritative
      }
    }

    return { tasks: fetchedTasks, timeLogs };
  }, [offlineEnabled]);

  const applyFetchResult = useCallback((data) => {
    setTasks(Array.isArray(data.tasks) ? data.tasks : []);
    setTimeLogs(Array.isArray(data.timeLogs) ? data.timeLogs : []);
  }, []);

  const mergeAll = useCallback((data, optimisticTimestamps, now) => {
    const merge = (setter, newData) => {
      if (!newData) return;
      setter(prev => {
        const { merged, anyChanged, expiredIds } = mergeWithOptimistic(prev, newData, optimisticTimestamps, now);
        for (const { id, age_ms } of expiredIds) logMergeDecision(id, 'ttl_expired', { age_ms });
        return anyChanged ? merged : prev;
      });
    };
    merge(setTasks, data.tasks);
    merge(setTimeLogs, data.timeLogs);
  }, []);

  return {
    tasks, setTasks,
    timeLogs, setTimeLogs,
    tasksRef,
    handleUpdateTask,
    handleUpdateStatus,
    handleUpdateAssignee,
    handleCreateTask,
    handleDeleteTask,
    handleArchiveTask,
    handleCreateTimeEntry,
    refreshTasks: setTasks,
    fetchAll,
    applyFetchResult,
    mergeAll,
  };
}
